import React, { useState } from 'react'
import Navbar from '../components/sections/Navbar'
import Footer from '../components/sections/Footer'
import LightRays from '../components/ui/LightRays'
import { useScrollAnimation } from '../hooks/useScrollAnimation'
import { useSEO } from '../hooks/useSEO'

// ─── Enquiry tabs ──────────────────────────────────────────────────────────────
const TABS = [
  { id: 'demo', label: 'Request Demo' },
  { id: 'partner', label: 'Become a Partner' },
  { id: 'others', label: 'Others' },
]

// ─── Content blocks ────────────────────────────────────────────────────────────
const BLOCKS = [
  {
    id: 'enterprise',
    iconVariant: 'primary',
    icon: (
      <svg width="22" height="22" viewBox="0 0 24 24" fill="none" aria-hidden="true">
        <path d="M3 21V9l9-7 9 7v12" stroke="#7C3AED" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round"/>
        <path d="M9 21v-6h6v6" stroke="#7C3AED" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round"/>
        <rect x="9" y="9" width="2" height="3" rx="0.5" stroke="#7C3AED" strokeWidth="1.2"/>
        <rect x="13" y="9" width="2" height="3" rx="0.5" stroke="#7C3AED" strokeWidth="1.2"/>
      </svg>
    ),
    title: 'Enterprise AI Solutions',
    desc: 'Custom AI architectures designed to fit your existing infrastructure and scale with your growth.'
  },
  {
    id: 'consulting',
    iconVariant: 'secondary',
    icon: (
      <svg width="22" height="22" viewBox="0 0 24 24" fill="none" aria-hidden="true">
        <polyline points="4,17 9,11 14,14 20,6" stroke="#06B6D4" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
        <circle cx="4" cy="17" r="1.2" fill="#06B6D4"/>
        <circle cx="9" cy="11" r="1.2" fill="#06B6D4"/>
        <circle cx="14" cy="14" r="1.2" fill="#06B6D4"/>
        <circle cx="20" cy="6" r="1.2" fill="#06B6D4"/>
        <path d="M4 21h16" stroke="#06B6D4" strokeWidth="1.3" strokeLinecap="round"/>
      </svg>
    ),
    title: 'AI Strategy Consulting',
    desc: 'Expert guidance to maximise AI ROI and competitive advantage across your organisation.'
  },
]

// ─── JSON-LD schema ────────────────────────────────────────────────────────────
const transformSchema = {
  '@context': 'https://schema.org',
  '@type': 'WebPage',
  url: 'https://augmentappz.ai/transform',
  name: 'AI-Native Transformation — AugmentAppz',
  description:
    'Enterprise AI solutions and strategy consulting to help your organization transition to AI-native operations.',
  publisher: {
    '@type': 'Organization',
    name: 'AugmentAppz',
    url: 'https://augmentappz.ai/',
  },
}

export default function TransformPage() {
  useSEO({
    title: 'AI-Native Transformation — AugmentAppz',
    description:
      'Enterprise AI solutions and AI strategy consulting from AugmentAppz. Transform your organization with intelligent systems built for scale.',
    keywords:
      'AI-native transformation, enterprise AI solutions, AI strategy consulting, AugmentAppz',
    canonical: 'https://augmentappz.ai/transform',
    jsonLd: transformSchema,
  })

  useScrollAnimation()

  const [form, setForm] = useState({
    firstName: '', lastName: '', company: '', jobTitle: '', email: '', message: '',
  })
  const [enquiryType, setEnquiryType] = useState('demo')
  const [errors, setErrors] = useState({})
  const [showPopup, setShowPopup] = useState(false)

  const validate = () => {
    const e = {}
    if (!form.firstName.trim()) e.firstName = 'First name is required'
    if (!form.lastName.trim()) e.lastName = 'Last name is required'
    if (!form.company.trim()) e.company = 'Company name is required'
    if (!form.jobTitle.trim()) e.jobTitle = 'Job title is required'
    if (!form.email.trim()) {
      e.email = 'Work email is required'
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email.trim())) {
      e.email = 'Enter a valid work email address'
    }
    if (!form.message.trim()) e.message = 'Message is required'
    return e
  }

  const handleChange = (e) => {
    const { name, value } = e.target
    setForm((prev) => ({ ...prev, [name]: value }))
    if (name === 'email') {
      if (value && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value.trim())) {
        setErrors((prev) => ({ ...prev, email: 'Enter a valid work email address' }))
      } else {
        setErrors((prev) => ({ ...prev, email: undefined }))
      }
    } else if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: undefined }))
    }
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    const errs = validate()
    if (Object.keys(errs).length > 0) {
      setErrors(errs)
      return
    }
    try {
      await fetch('https://api.web3forms.com/submit', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({
          access_key: 'REPLACE_WITH_WEB3FORMS_ACCESS_KEY',
          from_name: `${form.firstName} ${form.lastName}`,
          subject: `New enquiry from ${form.firstName} ${form.lastName} — ${form.company}`,
          'First Name': form.firstName,
          'Last Name': form.lastName,
          'Company': form.company,
          'Job Title': form.jobTitle,
          'Email': form.email,
          'Enquiry Type': enquiryType,
          'Message': form.message,
          replyto: form.email,
        }),
      })
    } catch (_) {
      // Silently continue — show success regardless to avoid leaking errors
    }
    setShowPopup(true)
    setForm({ firstName: '', lastName: '', company: '', jobTitle: '', email: '', message: '' })
    setErrors({})
    setEnquiryType('demo')
  }

  const closePopup = () => setShowPopup(false)

  return (
    <>
      <Navbar />
      <main aria-label="AI-Native Transformation page">

        {/* ── Hero band ─────────────────────────────────────────────────── */}
        <section className="transform-hero" aria-label="AI-Native Transformation">
          <LightRays
            raysOrigin="top-center"
            raysColor="#06b6d4"
            raysSpeed={0.8}
            lightSpread={1.2}
            rayLength={1.5}
            pulsating={true}
            fadeDistance={0.8}
            saturation={0.9}
            mouseInfluence={0.08}
          />
          <div className="container" style={{ position: 'relative', zIndex: 1 }}>
            <div className="about-hero-content" data-animate="fade-up">
              <div className="about-hero-accent" aria-hidden="true" />
              <h1 className="about-hero-title">AI-Native Transformation</h1>
              <p className="about-hero-sub">
                Transformation programs that help businesses redesign architectures<br />
                and operational models for AI-native execution.
              </p>
            </div>
          </div>
        </section>

        {/* ── Contact + Request Information — combined card ──────────────── */}
        <section className="section transform-main" aria-label="Contact and request information">
          <div className="container">
            <div className="contact-combined-card" data-animate="fade-up" data-delay="80">

              {/* Left panel */}
              <div className="contact-left-panel">

                {/* Top: tagline + feature highlights */}
                <div>
                  <p className="contact-info-tagline">
                    Let's architect your<br />AI-native future together.
                  </p>

                  <div className="tf-features">
                    <div className="tf-feature-item">
                      <div className="tf-feature-icon tf-feature-icon--primary">
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                          <path d="M3 21V9l9-7 9 7v12" stroke="#7C3AED" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round"/>
                          <path d="M9 21v-6h6v6" stroke="#7C3AED" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round"/>
                          <rect x="9" y="9" width="2" height="3" rx="0.5" stroke="#7C3AED" strokeWidth="1.2"/>
                          <rect x="13" y="9" width="2" height="3" rx="0.5" stroke="#7C3AED" strokeWidth="1.2"/>
                        </svg>
                      </div>
                      <div className="tf-feature-body">
                        <h3 className="tf-feature-title">Enterprise AI Solutions</h3>
                        <p className="tf-feature-desc">Custom AI architectures designed to fit your existing infrastructure and scale with your growth.</p>
                      </div>
                    </div>

                    <div className="tf-feature-item">
                      <div className="tf-feature-icon tf-feature-icon--violet">
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                          <polyline points="4,17 9,11 14,14 20,6" stroke="#9661F1" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                          <circle cx="4" cy="17" r="1" fill="#9661F1"/>
                          <circle cx="9" cy="11" r="1" fill="#9661F1"/>
                          <circle cx="14" cy="14" r="1" fill="#9661F1"/>
                          <circle cx="20" cy="6" r="1" fill="#9661F1"/>
                          <path d="M4 21h16" stroke="#9661F1" strokeWidth="1.2" strokeLinecap="round"/>
                        </svg>
                      </div>
                      <div className="tf-feature-body">
                        <h3 className="tf-feature-title">AI Strategy Consulting</h3>
                        <p className="tf-feature-desc">Expert guidance to maximise AI ROI and competitive advantage across your organisation.</p>
                      </div>
                    </div>
                  </div>
                </div>

              </div>

              {/* Right panel — dark floating form card */}
              <div className="contact-right-panel">
                <h2 className="contact-form-heading">Request Information</h2>
                <p className="contact-form-sub">
                  Fill out the form below and our team will get back to you within 24&nbsp;hours.
                </p>

                <form className="contact-form" onSubmit={handleSubmit} noValidate>
                  <div className="form-row">
                    <div className="form-field">
                      <label className="form-label" htmlFor="tf-firstName">
                        First Name <span aria-hidden="true">*</span>
                      </label>
                      <input
                        id="tf-firstName" name="firstName" type="text"
                        className={`form-input${errors.firstName ? ' form-input--error' : ''}`}
                        placeholder="Jane"
                        value={form.firstName} onChange={handleChange}
                      />
                      {errors.firstName && <span className="form-error-msg">{errors.firstName}</span>}
                    </div>
                    <div className="form-field">
                      <label className="form-label" htmlFor="tf-lastName">
                        Last Name <span aria-hidden="true">*</span>
                      </label>
                      <input
                        id="tf-lastName" name="lastName" type="text"
                        className={`form-input${errors.lastName ? ' form-input--error' : ''}`}
                        placeholder="Smith"
                        value={form.lastName} onChange={handleChange}
                      />
                      {errors.lastName && <span className="form-error-msg">{errors.lastName}</span>}
                    </div>
                  </div>

                  <div className="form-row">
                    <div className="form-field">
                      <label className="form-label" htmlFor="tf-company">
                        Company Name <span aria-hidden="true">*</span>
                      </label>
                      <input
                        id="tf-company" name="company" type="text"
                        className={`form-input${errors.company ? ' form-input--error' : ''}`}
                        placeholder="Acme Corp"
                        value={form.company} onChange={handleChange}
                      />
                      {errors.company && <span className="form-error-msg">{errors.company}</span>}
                    </div>
                    <div className="form-field">
                      <label className="form-label" htmlFor="tf-jobTitle">
                        Job Title <span aria-hidden="true">*</span>
                      </label>
                      <input
                        id="tf-jobTitle" name="jobTitle" type="text"
                        className={`form-input${errors.jobTitle ? ' form-input--error' : ''}`}
                        placeholder="CTO"
                        value={form.jobTitle} onChange={handleChange}
                      />
                      {errors.jobTitle && <span className="form-error-msg">{errors.jobTitle}</span>}
                    </div>
                  </div>

                  <div className="form-field">
                    <label className="form-label" htmlFor="tf-email">
                      Work Email <span aria-hidden="true">*</span>
                    </label>
                    <input
                      id="tf-email" name="email" type="email"
                      className={`form-input${errors.email ? ' form-input--error' : ''}`}
                      placeholder="jane@acme.com"
                      value={form.email} onChange={handleChange}
                    />
                    {errors.email && <span className="form-error-msg">{errors.email}</span>}
                  </div>

                  <div className="form-field">
                    <label className="form-label" htmlFor="tf-message">
                      Message <span aria-hidden="true">*</span>
                    </label>
                    <textarea
                      id="tf-message" name="message"
                      className={`form-input form-textarea${errors.message ? ' form-input--error' : ''}`}
                      placeholder="Tell us about your AI goals…"
                      rows={4}
                      value={form.message} onChange={handleChange}
                    />
                    {errors.message && <span className="form-error-msg">{errors.message}</span>}
                  </div>

                  {/* Enquiry type tabs */}
                  <div className="form-field">
                    <label className="form-label">I'm interested in</label>
                    <div className="tab-bar" role="tablist">
                      {TABS.map((t) => (
                        <button
                          key={t.id}
                          type="button"
                          role="tab"
                          aria-selected={enquiryType === t.id}
                          className={`tab-btn${enquiryType === t.id ? ' tab-btn--active' : ''}`}
                          onClick={() => setEnquiryType(t.id)}
                        >
                          {t.label}
                        </button>
                      ))}
                    </div>
                  </div>

                  <p className="form-privacy">
                    By submitting this form, you agree to our{' '}
                    <a href="https://augmentappz.ai/privacy" target="_blank" rel="noopener noreferrer">
                      Privacy Policy
                    </a>{' '}
                    and{' '}
                    <a href="https://augmentappz.ai/terms" target="_blank" rel="noopener noreferrer">
                      Terms of Service
                    </a>.
                  </p>

                  <button type="submit" className="btn btn-primary btn-lg contact-submit-btn">
                    Send Message
                    <svg className="btn-icon" width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true">
                      <path d="M3 8h10M9 4l4 4-4 4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  </button>
                </form>
              </div>

            </div>
          </div>
        </section>

      </main>
      <Footer />

      {/* ── Success popup ────────────────────────────────────────────── */}
      {showPopup && (
        <div
          className="success-popup-overlay"
          role="dialog"
          aria-modal="true"
          aria-label="Submission successful"
          onClick={(e) => { if (e.target === e.currentTarget) closePopup() }}
        >
          <div className="success-popup">
            <button
              className="success-popup-close"
              onClick={closePopup}
              aria-label="Close dialog"
            >
              <svg width="18" height="18" viewBox="0 0 18 18" fill="none" aria-hidden="true">
                <path d="M4 4l10 10M14 4L4 14" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
              </svg>
            </button>
            <div className="success-popup-icon" aria-hidden="true">
              <svg width="52" height="52" viewBox="0 0 52 52" fill="none">
                <circle cx="26" cy="26" r="24" stroke="#7C3AED" strokeWidth="2" />
                <path d="M15 26l8 8 14-15" stroke="#7C3AED" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </div>
            <h3 className="success-popup-title">Message Sent!</h3>
            <p className="success-popup-body">
              Thank you for reaching out. Our team will get back to you within 24 hours.
            </p>
            <button className="btn btn-primary" onClick={closePopup}>
              Done
            </button>
          </div>
        </div>
      )}
    </>
  )
}
